import { useQuery } from "@tanstack/react-query";

// MUI
import { Divider, Grid, Skeleton, Stack } from "@mui/material";

// API
import { getOEMData } from "@DataPages/api/oem";

// Helpers
import { getOEMParameters } from "@/Common/helpers/OEMs";

import OemDataPages from "@DataPages/components/OEM/Tables/OemDataTable/OemDataPages";
import CollapseArea from "@/Layouts/components/Basics/CollapseArea";
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";

// Styles
import "@DataPages/components/OEM/Tables/OemDataTable/OemDataTable.css";

// Assets
import empty from "@/assets/images/commons/empty.png";

// Types
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";
import { IOEM } from "@Common/interfaces/location";
import { FilterRankingDataPageProps, RowArray } from "@DataPages/components/OEM/OEMTabBody";

type NoDataProps = Readonly<{
	emptyTitle: string;
	oemDataTab: string;
}>;

type OEMDocumentsTabProps = Readonly<{
	selected: ICommissionCycle;
	locationId: string;
	locationOEMs: IOEM[];
	oemDataTab: string;
	tableProps?: object;
	emptyTitle?: string;
	filterRankingDataPage?: ({ content, tableInformation }: Readonly<FilterRankingDataPageProps>) => RowArray;
}>;

/******************************************** HELPERS COMPONENTS ********************************************/
const NoData = (props: NoDataProps) => {
	return (
		<EmptyBody
			image={empty}
			title={`No ${props.emptyTitle || props.oemDataTab} data found`}
			imageStyles={{ width: "330px", marginTop: "65px" }}
			bodyHeight={"320px"}
			sx={{ height: "500px !important", boxShadow: "none !important" }}
		/>
	);
};

const LoadSkeleton = () => {
	return (
		<Grid item mt={0} xs={12} mb={2} marginTop={5} paddingX={4} height={500}>
			<Skeleton variant="rectangular" width={200} height={20} sx={{ borderRadius: "6px", marginTop: "20px", marginBottom: 2 }} />
			<TableLoader content rows={7} toolbar buttons={3} bigTitle />
		</Grid>
	);
};

export default function OEMDocumentsTab(props: OEMDocumentsTabProps) {
	const parentsProps = { commissionCycleId: props.selected?._id, locationId: props.locationId, locationOEMs: props?.locationOEMs };

	const { data: oems, isLoading } = useQuery({
		queryKey: ["oemData", props.locationId, props.selected?._id, props.oemDataTab],
		queryFn: async () =>
			getOEMData({
				locationId: props.locationId,
				commissionCycleId: props.selected?._id,
				programName: props.oemDataTab,
			}),
		enabled: Boolean(props.selected?._id) && Boolean(props.locationId) && Boolean(props.oemDataTab),
		staleTime: Infinity,
	});

	if (isLoading) {
		return <LoadSkeleton />;
	}

	if (!oems?.pages?.length) {
		return <NoData emptyTitle={props.emptyTitle ?? ""} oemDataTab={props.oemDataTab} />;
	}

	return (
		<Stack sx={{ minHeight: "500px !important" }}>
			{oems?.pages?.map((oem, key) => {
				const oemParams = getOEMParameters({
					oemReference: oem?.oemReference,
					dealerCodeFS: oem?.dealerCodeFS,
					locationOEMs: props?.locationOEMs,
				}) as { oemDisplayName: string };

				const { oemDisplayName } = oemParams || {};
				return (
					<Stack className="oem-data-tab-collapse" key={oem.oemReference}>
						<CollapseArea
							title={oemDisplayName || oem.oemName}
							sxTitle={{ marginLeft: 5.5, color: "#2357BE !important" }}
							collapsible={oems?.pages?.length > 1}
							openCollapse={oems?.pages?.length === 1}
						>
							<OemDataPages
								locationId={props.locationId}
								selected={props.selected}
								oemDataTab={props.oemDataTab}
								isButtonsPaginationPage
								franchiseName={oem.franchise}
								franchiseNames={oem.franchiseNames ?? []}
								dealerCodeFS={oem.dealerCodeFS}
								oem={oem.oemReference}
								currentPageNumbers={oem.pageNames ?? []}
								defaultPage={oem.page}
								parentsProps={{ ...parentsProps, oemReference: oem.oemReference, dealerCodeFS: oem?.dealerCodeFS }}
								filterRankingDataPage={props.filterRankingDataPage}
								tableProps={props?.tableProps}
							/>
						</CollapseArea>
						{Boolean(key !== oems?.pages?.length - 1) && (
							<Divider className="!border-disabled" sx={{ borderWidth: "1px", marginX: 5 }} variant="middle" />
						)}
					</Stack>
				);
			})}
		</Stack>
	);
}
